import { useMemo, useState, useEffect } from 'react';
import { useGetProductsQuery } from '../services/api';
import { Product } from '../types';
import { fetchRuntimeConfig } from '../services/config';

interface UseRelatedProductsOptions {
  product: Product | undefined;
  count: number;
}

/**
 * Fetches related products for the PDP.
 * Strategy: same collection_id as the current product, excluding self.
 * Falls back to latest products if no collection match.
 */
export const useRelatedProducts = ({ product, count }: UseRelatedProductsOptions) => {
  const [regionId, setRegionId] = useState<string | undefined>();

  useEffect(() => {
    fetchRuntimeConfig().then((config) => {
      if (config.medusa.regionId) {
        setRegionId(config.medusa.regionId);
      }
    }).catch(() => {});
  }, []);

  const collectionId = product?.collection_id;

  // Fetch by collection_id when available
  const { data: collectionData, isLoading: collectionLoading } = useGetProductsQuery(
    {
      collection_id: collectionId!,
      limit: count + 1, // +1 to account for excluding current product
      region_id: regionId,
    },
    { skip: !product || !collectionId || !regionId },
  );

  // Fallback: fetch latest products when no collection match
  const { data: fallbackData, isLoading: fallbackLoading } = useGetProductsQuery(
    {
      limit: count + 1,
      region_id: regionId,
    },
    { skip: !product || !!collectionId || !regionId },
  );

  const relatedProducts = useMemo(() => {
    const sourceProducts = collectionId
      ? collectionData?.products
      : fallbackData?.products;

    if (!sourceProducts || !product) return [];

    return sourceProducts
      .filter((p) => p.id !== product.id)
      .slice(0, count);
  }, [collectionData, fallbackData, product, collectionId, count]);

  const isLoading = collectionId ? collectionLoading : fallbackLoading;

  return { relatedProducts, isLoading };
};
